/**
 * AJUSTAR-TEXTO-AL-ANCHO — Helper SSOT sobre fitty para el front-tv.
 *
 * Escala el font-size de un elemento para que su texto LLENE el ancho de su
 * contenedor en UNA sola línea (sin partir, sin interlineado que rompa la
 * estética). Una sola forma de hacerlo en todo el front-tv: defaults coherentes
 * y manejo del ciclo de vida (subscribe/unsubscribe) en un solo lugar.
 *
 * Apoyado en fitty (vendor local). fitty mide parentNode.clientWidth, así que el
 * "aire" lateral se da con padding en el CONTENEDOR del elemento ajustado.
 *
 * Hardware: el WebView de las TVs es v101+ (Chromium moderno). fitty usa
 * ResizeObserver/MutationObserver, soportados. Recalcula solo ante cambio de
 * texto o resize — sin coste por frame.
 */

import fitty from '../vendor/fitty.js';

// Defaults del front-tv: una línea siempre (multiLine:false → se achica hasta
// minSize antes que partir), topes que evitan texto ilegible o desbordante.
const DEFAULTS = {
    minSize: 12,
    maxSize: 64,
    multiLine: false,
};

/**
 * Ajusta UN elemento para que su texto llene el ancho. El elemento debe ser el
 * que lleva el texto (no su contenedor con padding). Devuelve la instancia fitty
 * (con .unsubscribe() / .fit() / .freeze()) para manejar su ciclo de vida.
 *
 * @param {HTMLElement} elemento
 * @param {{minSize?:number, maxSize?:number, multiLine?:boolean}} [opciones]
 * @returns {object|null} instancia fitty, o null si el elemento no existe
 */
export function ajustarTextoAlAncho(elemento, opciones) {
    if (!elemento) return null;
    return fitty(elemento, { ...DEFAULTS, ...opciones });
}

/**
 * Ajusta VARIOS elementos (ej. las líneas de características del resultado, que
 * se recrean cada sorteo). Devuelve el array de instancias fitty.
 *
 * Para elementos que se reemplazan con innerHTML: guardá el array devuelto y
 * pasalo a desuscribir() antes de recrear el HTML, para no dejar observers
 * apuntando a nodos huérfanos.
 *
 * @param {NodeList|HTMLElement[]} elementos
 * @param {{minSize?:number, maxSize?:number, multiLine?:boolean}} [opciones]
 * @returns {object[]} instancias fitty
 */
export function ajustarVariosAlAncho(elementos, opciones) {
    const lista = [];
    for (let i = 0; i < elementos.length; i++) {
        const instancia = fitty(elementos[i], { ...DEFAULTS, ...opciones });
        if (instancia) lista.push(instancia);
    }
    return lista;
}

/**
 * Desconecta las instancias fitty (sus observers) antes de recrear los nodos.
 * Acepta una instancia única o un array. Idempotente y tolerante a null.
 *
 * @param {object|object[]|null} instancias
 */
export function desuscribir(instancias) {
    if (!instancias) return;
    const lista = Array.isArray(instancias) ? instancias : [instancias];
    for (let i = 0; i < lista.length; i++) {
        if (lista[i] && typeof lista[i].unsubscribe === 'function') {
            lista[i].unsubscribe();
        }
    }
}

export default { ajustarTextoAlAncho, ajustarVariosAlAncho, desuscribir };
